import type { CrewMember } from "../../../api/crewSchemas";
import { allowedClockEvents } from "../../../domain/clock";
import type { EpochSeconds } from "../../../domain/time";
import { crewMemberClock } from "../../../offline/crew";
import type { CrewTarget } from "../../../offline/crew";
import type { LocalEvent } from "../../../offline/model";
import { isOwnEvent } from "../../../offline/ownEvents";
import type { ClockAction } from "./clockActions";

/**
 * The steps of the crew in the order the screen offers them (T-170): a break is recorded as the
 * statutory rest break, never unclassified (T-150); standby stays a step each person records.
 */
export const crewSteps: readonly ClockAction[] = [
  { kind: "ClockIn", breakQualification: null },
  { kind: "BreakStart", breakQualification: "RestBreak" },
  { kind: "BreakEnd", breakQualification: null },
  { kind: "ClockOut", breakQualification: null },
];

/** A person of the crew with the local state shown and checked. */
export interface CrewEntry extends CrewTarget {
  /** Instant of the last event, `null` if none is known. */
  readonly since: EpochSeconds | null;
  /** Events recorded for the person on this device that are not confirmed yet. */
  readonly pending: number;
}

/**
 * Returns the persons of the crew with their local state (the crew's state plus the events recorded
 * for them on this device that it does not reflect yet).
 *
 * @param members - The persons of the crew.
 * @param fetchedAtMs - Device time when the crew was requested, in milliseconds.
 * @param events - Local events of the signed-in person.
 * @returns The persons in the crew's order.
 */
export function crewEntries(
  members: readonly CrewMember[],
  fetchedAtMs: number,
  events: readonly LocalEvent[],
): readonly CrewEntry[] {
  const entries = members.map((member): CrewEntry => {
    const clock = crewMemberClock(member, fetchedAtMs, events);
    const entry: CrewEntry = { member, ...clock };

    return entry;
  });

  return entries;
}

/**
 * Returns the events on behalf of others the server rejected.
 *
 * @param events - Local events of the signed-in person.
 * @returns The rejected events on behalf.
 */
export function crewConflicts(events: readonly LocalEvent[]): readonly LocalEvent[] {
  const conflicts = events.filter((event) => !isOwnEvent(event) && event.syncStatus === "rejected");
  return conflicts;
}

/**
 * Returns the chosen persons whose state allows a step.
 *
 * @param step - The step.
 * @param targets - The chosen persons with their local state.
 * @returns The persons the step is recorded for.
 */
export function targetsOf<T extends CrewTarget>(
  step: ClockAction,
  targets: readonly T[],
): readonly T[] {
  const allowed = targets.filter((target) => allowedClockEvents(target.state).includes(step.kind));

  return allowed;
}

/**
 * Filters the crew by a search text over name and personnel number, ignoring case.
 *
 * @param targets - The crew.
 * @param search - The search text; empty keeps everyone.
 * @returns The matching persons.
 */
export function matchingTargets<T extends CrewTarget>(
  targets: readonly T[],
  search: string,
): readonly T[] {
  const needle = search.trim().toLocaleLowerCase();
  const matching =
    needle === ""
      ? targets
      : targets.filter(
          (target) =>
            target.member.displayName.toLocaleLowerCase().includes(needle) ||
            target.member.personnelNumber.toLocaleLowerCase().includes(needle),
        );

  return matching;
}

/**
 * Adds the shown persons to the selection; persons chosen before and hidden by the search stay chosen.
 *
 * @param selected - The current selection.
 * @param shown - The persons shown.
 * @returns The new selection.
 */
export function withShown(
  selected: ReadonlySet<string>,
  shown: readonly CrewTarget[],
): ReadonlySet<string> {
  const next = new Set([...selected, ...shown.map((target) => target.member.employeeId)]);
  return next;
}
